/**
 * Pure helpers for the post-accession comparator-addition window on
 * `<OrderInProgress>`.
 *
 * Business rule (all conditions must hold for the window to be OPEN):
 *   1. The order is an ULTRA-RAPID (urWGS) order — Rapid (rWGS) and others never
 *      get the window.
 *   2. The order is accessioned (`OrderStatusDisplay.SAMPLE_RECEIVED`).
 *   3. The tenant has the editability window enabled
 *      (`ui_config.editabilityWindow.editabilityWindowEnabled`).
 *   4. We are before the deadline = proband accession time +
 *      `comparatorAdditionWindowHours`.
 *
 * When open, the page re-opens EXACTLY ONE affordance the status gate would
 * otherwise freeze at accession: adding NEW comparators. It never unlocks
 * proband-field edits or existing-comparator edit/remove — those stay gated by
 * the page's `canEditIndications` / `canEditComparatorParents`.
 *
 * These helpers are pure (all inputs passed explicitly, `now` injectable) so
 * they can be unit-tested without mounting the ~3,900-LOC page, and so the
 * "open?" gate and the banner label stay derived from a single source of truth.
 */

import {
  getComparatorAdditionWindowHours,
  isComparatorAdditionWindowEnabled,
  isUltraRapidTestType,
  type UiConfig,
} from '@/config/uiConfig'
import { OrderStatusDisplay } from '@/types/NavigationTypes'

const MS_PER_HOUR = 60 * 60 * 1000

export interface ComparatorAdditionWindowInput {
  /** Display status derived from the order status response. */
  orderStatus: OrderStatusDisplay | null | undefined
  /** The order's persisted test-type SKU (`canonical.order.selectedOption`). */
  selectedOption: string | null | undefined
  /** ISO accession timestamp (`accessioned_on`); null when not yet accessioned. */
  accessionedOn: string | null | undefined
  /** Active UI config (tenant window flag + hours). */
  config: UiConfig | undefined
}

/**
 * The deadline (epoch ms) after which no further comparators may be added, or
 * `null` when the window does not apply to this order at all (not ultra-rapid,
 * feature disabled, not accessioned, or missing/invalid timestamp / hours).
 *
 * Note: this intentionally does NOT consider "now" — it only resolves the
 * boundary. Callers decide open-vs-passed via {@link isComparatorAdditionWindowOpen}
 * / {@link resolveComparatorAdditionWindow}.
 */
export function comparatorAdditionDeadlineMs(input: ComparatorAdditionWindowInput): number | null {
  const { orderStatus, selectedOption, accessionedOn, config } = input
  if (orderStatus !== OrderStatusDisplay.SAMPLE_RECEIVED) return null
  if (!isUltraRapidTestType(selectedOption)) return null
  if (!isComparatorAdditionWindowEnabled(config)) return null
  if (!accessionedOn) return null
  const windowHours = getComparatorAdditionWindowHours(config)
  if (windowHours === undefined) return null
  const accessionedMs = new Date(accessionedOn).getTime()
  if (!Number.isFinite(accessionedMs)) return null
  return accessionedMs + windowHours * MS_PER_HOUR
}

/**
 * Whether the comparator-addition window is currently OPEN — i.e. the window
 * applies to this order AND `now` is at/before the deadline. Fails safe to
 * `false` whenever the window does not apply.
 */
export function isComparatorAdditionWindowOpen(
  input: ComparatorAdditionWindowInput,
  now: number = Date.now()
): boolean {
  const deadlineMs = comparatorAdditionDeadlineMs(input)
  if (deadlineMs === null) return false
  return now <= deadlineMs
}

/**
 * Banner-facing resolution of the window: a human-readable deadline `label` and
 * whether it has already `passed`. Returns `null` when the window does not
 * apply to this order (so the banner omits all window messaging). Note this is
 * `null` for the "does not apply" case but a real object even once the deadline
 * has passed (so the banner can say "no further additions"); distinguish the
 * two via `passed`.
 */
export function resolveComparatorAdditionWindow(
  input: ComparatorAdditionWindowInput,
  now: number = Date.now()
): { label: string; passed: boolean } | null {
  const deadlineMs = comparatorAdditionDeadlineMs(input)
  if (deadlineMs === null) return null
  const deadline = new Date(deadlineMs)
  return {
    label: `${deadline.toLocaleDateString('en-US')} at ${deadline.toLocaleTimeString('en-US')}`,
    passed: now > deadlineMs,
  }
}
